import React, { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";
import { TrendingUp } from "lucide-react";
import "./ChatBot.scss";

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [currentView, setCurrentView] = useState("home"); // 'home' or 'chat'
  const [messages, setMessages] = useState([
    {
      role: "ai",
      content: `${getGreeting()} and welcome to BizAura! We specialize in managing business communications and building digital solutions. How can we assist you today?`,
      isHtml: false,
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [showEmojiTray, setShowEmojiTray] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showOnlineMsg, setShowOnlineMsg] = useState(true);
  const [leadFormData, setLeadFormData] = useState({
    name: "",
    contact: "",
    details: "",
    preference: "",
  });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);

  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  const knowledgeBase = {
    company: {
      name: "BizAura",
      email: "info@bizaura.com",
      phone: "+91 9510270718",
    },
    faqs: [
      {
        q: "what is your company name?",
        a: "We are BizAura, a comprehensive business management platform.",
      },
      {
        q: "what services do you provide?",
        a: "We provide billing management, subscription tracking, and business communication tools.",
      },
      {
        q: "how can i contact you?",
        a: "You can contact us via email at info@bizaura.com or phone +91 9510270718.",
      },
      {
        q: "how much does it cost?",
        a: "Pricing depends on your plan. Please check our pricing page for details.",
      },
    ],
  };

  function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  }

  useEffect(() => {
    emailjs.init("ZjLrQuwwbCAEeh1Sp");

    const savedName = localStorage.getItem("bizaura_user_name");
    if (savedName) setLeadFormData((prev) => ({ ...prev, name: savedName }));
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setShowOnlineMsg(false);
    if (isOpen) setIsZoomed(false);
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    const text = inputValue.trim();
    if (!text) return;

    const newMessages = [
      ...messages,
      { role: "user", content: text, isHtml: false },
    ];
    setMessages(newMessages);
    setInputValue("");
    setShowEmojiTray(false);

    setIsTyping(true);

    // AI Response logic
    setTimeout(async () => {
      const query = text.toLowerCase();
      let response = {
        content:
          "Hello! Please describe your use case in detail so I can help you effectively.",
        isHtml: false,
      };

      const priceKeywords = [
        "quote",
        "price",
        "cost",
        "how much",
        "pricing",
        "rate",
      ];
      if (priceKeywords.some((key) => query.includes(key))) {
        response = {
          content: `<div class="strongMessage">Please fill out this form and our team will contact you shortly!</div><p>How would you prefer to be contacted?</p>`,
          isHtml: true,
          isForm: "preference",
        };
      } else {
        const match = knowledgeBase.faqs.find((faq) =>
          query.includes(faq.q.toLowerCase()),
        );
        if (match) response = { content: match.a, isHtml: false };
      }

      setIsTyping(false);
      setMessages((prev) => [...prev, response]);
    }, 1000);
  };

  const handlePreferenceSelect = (pref) => {
    setMessages((prev) => [
      ...prev,
      {
        role: "ai",
        content: `Selected: ${pref.charAt(0).toUpperCase() + pref.slice(1)}`,
        isHtml: true,
        isForm: "lead",
        preference: pref,
      },
    ]);
  };

  const handleLeadSubmit = async (e, preference) => {
    e.preventDefault();
    const form = e.target;
    const name = form.name.value;
    const contact = form.contact.value;
    const details = form.details.value;

    if (!name || !contact || !details) return;

    setIsSubmittingLead(true);

    try {
      await emailjs.send("service_ewfj14a", "template_loj0i1i", {
        name,
        message: details,
        [preference === "email" ? "email" : "phone"]: contact,
        date: new Date().toLocaleDateString(),
      });

      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          content:
            "✅ Thank you! Your request has been recorded. Our team will contact you shortly.",
          isHtml: false,
        },
      ]);
      localStorage.setItem("bizaura_user_name", name);
    } catch (err) {
      console.error(err);
      alert("Failed to send inquiry. Please try again.");
    } finally {
      setIsSubmittingLead(false);
    }
  };

  return (
    <>
      {showOnlineMsg && !isOpen && (
        <div className="chatOnlineMsg">
          <span className="msgTitle">We're Online!</span>
          <span className="msgBody">How may I help you today?</span>
        </div>
      )}

      <button
        className={`chatToggle ${isOpen ? "active" : ""}`}
        onClick={toggleChat}
        aria-label="Toggle Chat"
      >
        <i className={`fas ${isOpen ? "fa-times" : "fa-comment-dots"}`}></i>
      </button>

      {isOpen && (
        <div className={`chatContainer active ${isZoomed ? "zoomed" : ""}`}>
          <div className="chatHeader">
            <div className="headerLeft">
              {currentView === "chat" && (
                <i
                  className="fas fa-arrow-left"
                  onClick={() => setCurrentView("home")}
                ></i>
              )}
            </div>
            <div className="headerCapsule">
              <TrendingUp
                size={24}
                color="#a0d585"
                style={{ marginRight: "8px" }}
              />
              <span
                style={{
                  fontWeight: "800",
                  fontSize: "18px",
                  color: "#263b6a",
                }}
              >
                BizAura
              </span>
            </div>
            <div className="headerRight">
              <i
                className={`fas ${isZoomed ? "fa-compress-alt" : "fa-expand-alt"}`}
                onClick={() => setIsZoomed(!isZoomed)}
              ></i>
            </div>
          </div>

          <div className="viewViewport">
            {/* Home View */}
            <div
              className={`viewContainer ${currentView === "home" ? "active" : ""}`}
            >
              <div className="homeHeader">
                <h1>Chat with us!</h1>
              </div>
              <div className="homeContent">
                <div className="welcomeCard">
                  <div className="cardTop">
                    <div className="avatarStack">
                      <TrendingUp size={24} color="#a0d585" />
                    </div>
                    <div className="cardInfo">
                      <span className="supportLabel">
                        Text Support • Active Now
                      </span>
                      <p>
                        Hello! Please let me know how I can assist you with our
                        products or services. Feel free to describe your use
                        case...
                      </p>
                    </div>
                  </div>
                  <button
                    className="startChatBtn"
                    onClick={() => setCurrentView("chat")}
                  >
                    Start Chat <i className="fas fa-paper-plane"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Chat View */}
            <div
              className={`viewContainer ${currentView === "chat" ? "active" : ""}`}
            >
              <div className="chatMessages">
                {messages.map((msg, i) => (
                  <div key={i} className={`messageWrapper ${msg.role}`}>
                    {msg.role === "ai" && (
                      <div className="aiAvatar">
                        <TrendingUp size={16} />
                      </div>
                    )}
                    <div className={`message ${msg.role}`}>
                      {msg.isHtml ? (
                        <div
                          dangerouslySetInnerHTML={{ __html: msg.content }}
                        />
                      ) : (
                        msg.content
                      )}

                      {msg.isForm === "preference" && (
                        <div className="preferenceOptions">
                          <button
                            className="preferenceBtn"
                            onClick={() => handlePreferenceSelect("email")}
                          >
                            <i className="fas fa-envelope"></i> Email
                          </button>
                          <button
                            className="preferenceBtn"
                            onClick={() => handlePreferenceSelect("mobile")}
                          >
                            <i className="fas fa-mobile-alt"></i> Mobile
                          </button>
                        </div>
                      )}

                      {msg.isForm === "lead" && (
                        <form
                          className="leadForm"
                          onSubmit={(e) => handleLeadSubmit(e, msg.preference)}
                        >
                          <input
                            name="name"
                            type="text"
                            placeholder="Full Name *"
                            defaultValue={leadFormData.name}
                            required
                          />
                          <input
                            name="contact"
                            type={msg.preference === "email" ? "email" : "tel"}
                            placeholder={
                              msg.preference === "email"
                                ? "Email Address *"
                                : "Mobile Number *"
                            }
                            required
                          />
                          <textarea
                            name="details"
                            placeholder="Describe your project... *"
                            rows="3"
                            required
                          ></textarea>
                          <button type="submit" disabled={isSubmittingLead}>
                            {isSubmittingLead ? "Sending..." : "Submit Inquiry"}
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="messageWrapper ai">
                    <div className="aiAvatar">
                      <TrendingUp size={16} />
                    </div>
                    <div className="message ai typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {showEmojiTray && (
                <div className="emojiTray active">
                  {["😊", "😂", "❤️", "👍", "🔥", "🤔", "🙌", "✨"].map(
                    (emoji) => (
                      <span
                        key={emoji}
                        className="emojiItem"
                        onClick={() => {
                          setInputValue((prev) => prev + emoji);
                          setShowEmojiTray(false);
                          chatInputRef.current?.focus();
                        }}
                      >
                        {emoji}
                      </span>
                    ),
                  )}
                </div>
              )}

              <form className="chatInputWrapper" onSubmit={handleSendMessage}>
                <div className="inputGroup">
                  <input
                    ref={chatInputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Write a message..."
                    autoComplete="off"
                  />
                  <i
                    className="far fa-smile"
                    onClick={() => setShowEmojiTray(!showEmojiTray)}
                  ></i>
                </div>
                <button
                  className={`sendBtnCircle ${inputValue.trim() ? "active" : ""}`}
                  type="submit"
                >
                  <i className="fas fa-paper-plane"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
